import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { EstadoCuentaTareasPendientes } from '../components/admin/EstadoCuentaTareasPendientes';
import { 
  Users, GraduationCap, Scale, Building, TrendingUp, 
  UserCheck, UserX, Clock, Award, FileText, BookOpen,
  BarChart3, PieChart, Calendar, X, Loader2, DollarSign,
  Mail, CheckCircle, AlertCircle, TrendingDown, Download,
  RefreshCw,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Colores para gráficos
const COLORS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-purple-500', 'bg-amber-500', 
  'bg-pink-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500',
  'bg-cyan-500', 'bg-rose-500'
];

// Mapeo de colores a clases Tailwind
const colorClasses = {
  blue: { text: 'text-blue-600', bg: 'bg-blue-100' },
  emerald: { text: 'text-emerald-600', bg: 'bg-emerald-100' },
  purple: { text: 'text-purple-600', bg: 'bg-purple-100' },
  amber: { text: 'text-amber-600', bg: 'bg-amber-100' },
  pink: { text: 'text-pink-600', bg: 'bg-pink-100' },
  red: { text: 'text-red-600', bg: 'bg-red-100' },
  indigo: { text: 'text-indigo-600', bg: 'bg-indigo-100' },
};

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'blue', onClick }) => {
  const colors = colorClasses[color] || colorClasses.blue;
  return (
    <Card 
      className={`card-hover ${onClick ? 'cursor-pointer hover:shadow-lg transition-shadow' : ''}`}
      onClick={onClick}
    >
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className={`text-3xl font-bold ${colors.text}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
            {onClick && <p className="text-xs text-blue-500 mt-1">Click para ver detalle</p>}
          </div>
          <div className={`w-12 h-12 rounded-full ${colors.bg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${colors.text}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const BarChart = ({ data, labelKey, valueKey, title }) => {
  if (!data || data.length === 0) return <p className="text-gray-400 text-center py-4">Sin datos</p>;
  
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-3">
      {data.map((item, index) => (
        <div key={item[labelKey] || index} className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${COLORS[index % COLORS.length]}`} />
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                {item[labelKey]}
              </span>
              <span className="text-sm text-gray-500">{item[valueKey]}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${COLORS[index % COLORS.length]}`}
                style={{ width: `${(item[valueKey] / maxValue) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminEstadisticas() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [showModalCuentas, setShowModalCuentas] = useState(false);
  const [matriculadosConCuenta, setMatriculadosConCuenta] = useState([]);
  const [loadingModal, setLoadingModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatriculadosConCuenta = async () => {
    setLoadingModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/matriculados-con-cuenta`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Ordenar por fecha de registro (más reciente primero)
      const matriculados = response.data.matriculados || [];
      matriculados.sort((a, b) => {
        const fechaA = a.fecha_registro ? new Date(a.fecha_registro) : new Date(0);
        const fechaB = b.fecha_registro ? new Date(b.fecha_registro) : new Date(0);
        return fechaB - fechaA; // Más reciente primero
      });
      setMatriculadosConCuenta(matriculados);
      setShowModalCuentas(true);
    } catch (err) {
      console.error('Error fetching matriculados:', err);
    } finally {
      setLoadingModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-gray-500">
        Error al cargar estadísticas
      </div>
    );
  }

  const tabs = [
    { id: 'general', label: 'Resumen General', icon: BarChart3 },
    { id: 'estado-cuenta', label: 'Estado de Cuenta', icon: DollarSign },
    { id: 'capacitaciones', label: 'Capacitaciones', icon: GraduationCap },
    { id: 'auxiliares', label: 'Auxiliares de Justicia', icon: Scale },
    { id: 'recursero', label: 'Recursero', icon: Building },
    { id: 'matriculados', label: 'Matriculadxs', icon: Users },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Estadísticas</h1>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-4">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-emerald-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ========== RESUMEN GENERAL ========== */}
      {activeTab === 'general' && (
        <div className="space-y-8">
          {/* Cards resumen */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Matriculadxs" 
              value={stats.matriculados?.total || 0} 
              icon={Users} 
              color="blue"
            />
            <StatCard 
              title="Inscripciones a Cursos" 
              value={stats.capacitaciones?.total_inscripciones || 0} 
              subtitle={`${stats.capacitaciones?.total_asistentes || 0} asistentes confirmados`}
              icon={GraduationCap} 
              color="emerald"
            />
            <StatCard 
              title="Auxiliares de Justicia" 
              value={stats.auxiliares?.total || 0} 
              subtitle={`${stats.auxiliares?.aprobados || 0} aprobados`}
              icon={Scale} 
              color="purple"
            />
            <StatCard 
              title="Instituciones" 
              value={stats.instituciones?.total || 0} 
              subtitle={`${stats.instituciones?.activas || 0} activas`}
              icon={Building} 
              color="amber"
            />
          </div>

          {/* Gráficos por formación */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="w-5 h-5 text-emerald-600" />
                  Inscripciones por Formación Académica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.capacitaciones?.por_formacion || []} 
                  labelKey="formacion" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Scale className="w-5 h-5 text-purple-600" />
                  Auxiliares por Tipo de Perito
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.auxiliares?.por_tipo_perito || []} 
                  labelKey="tipo" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ========== ESTADO DE CUENTA ========== */}
      {activeTab === 'estado-cuenta' && (
        <EstadoCuentaStats />
      )}

      {/* ========== CAPACITACIONES ========== */}
      {activeTab === 'capacitaciones' && (
        <div className="space-y-8">
          {/* Cards principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <StatCard 
              title="Total Cursos" 
              value={stats.capacitaciones?.total_cursos || 0} 
              icon={BookOpen} 
              color="blue"
            />
            <StatCard 
              title="Cursos Activos" 
              value={stats.capacitaciones?.cursos_activos || 0} 
              icon={TrendingUp} 
              color="emerald"
            />
            <StatCard 
              title="Inscripciones" 
              value={stats.capacitaciones?.total_inscripciones || 0} 
              icon={Users} 
              color="purple"
            />
            <StatCard 
              title="Asistentes" 
              value={stats.capacitaciones?.total_asistentes || 0} 
              icon={UserCheck} 
              color="amber"
            />
            <StatCard 
              title="Certificados Emitidos" 
              value={stats.capacitaciones?.certificados_emitidos || 0} 
              icon={Award} 
              color="pink"
            />
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="w-5 h-5 text-emerald-600" />
                  Por Formación Académica
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.capacitaciones?.por_formacion || []} 
                  labelKey="formacion" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                  Cursos Más Populares
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.capacitaciones?.cursos_populares || []} 
                  labelKey="nombre" 
                  valueKey="inscripciones" 
                />
              </CardContent>
            </Card>
          </div>

          {/* Inscripciones por mes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Inscripciones por Mes (últimos 6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.capacitaciones?.inscripciones_por_mes?.length > 0 ? (
                <div className="flex items-end gap-2 h-40">
                  {stats.capacitaciones.inscripciones_por_mes.map((item, index) => {
                    const maxVal = Math.max(...stats.capacitaciones.inscripciones_por_mes.map(i => i.count));
                    const height = maxVal > 0 ? (item.count / maxVal) * 100 : 0;
                    return (
                      <div key={item.mes} className="flex-1 flex flex-col items-center">
                        <span className="text-xs text-gray-500 mb-1">{item.count}</span>
                        <div 
                          className="w-full bg-emerald-500 rounded-t transition-all duration-500"
                          style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                        />
                        <span className="text-xs text-gray-400 mt-2 transform -rotate-45 origin-left">
                          {item.mes?.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Sin datos de los últimos meses</p>
              )}
            </CardContent>
          </Card>

          {/* Inscriptos por Curso con Distrito */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="w-5 h-5 text-violet-600" />
                Inscriptos por Curso y Distrito
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.capacitaciones?.inscriptos_por_curso?.length > 0 ? (
                <div className="space-y-4">
                  {stats.capacitaciones.inscriptos_por_curso.map((curso) => (
                    <div key={curso.curso_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{curso.curso_nombre}</h4>
                          <p className="text-sm text-gray-500">Total inscriptos: <span className="font-bold text-violet-600">{curso.total_inscriptos}</span></p>
                        </div>
                      </div>
                      {curso.por_distrito?.length > 0 ? (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                          {curso.por_distrito.map((d, idx) => (
                            <div key={idx} className="bg-gray-50 rounded px-3 py-2 text-sm flex justify-between">
                              <span className="text-gray-600 truncate">{d.distrito}</span>
                              <span className="font-semibold text-violet-700 ml-2">{d.count}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Sin datos de distrito</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Sin inscripciones en cursos</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== AUXILIARES DE JUSTICIA ========== */}
      {activeTab === 'auxiliares' && (
        <div className="space-y-8">
          {/* Cards principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              title="Total Registros" 
              value={stats.auxiliares?.total || 0} 
              icon={Scale} 
              color="purple"
            />
            <StatCard 
              title="Pendientes" 
              value={stats.auxiliares?.pendientes || 0} 
              icon={Clock} 
              color="amber"
            />
            <StatCard 
              title="Aprobados" 
              value={stats.auxiliares?.aprobados || 0} 
              icon={UserCheck} 
              color="emerald"
            />
            <StatCard 
              title="Rechazados" 
              value={stats.auxiliares?.rechazados || 0} 
              icon={UserX} 
              color="red"
            />
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Scale className="w-5 h-5 text-purple-600" />
                  Por Tipo de Perito
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.auxiliares?.por_tipo_perito || []} 
                  labelKey="tipo" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="w-5 h-5 text-indigo-600" />
                  Por Título/Formación
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.auxiliares?.por_titulo || []} 
                  labelKey="titulo" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>
          </div>

          {/* Registros por mes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Registros por Mes (últimos 6 meses)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.auxiliares?.registros_por_mes?.length > 0 ? (
                <div className="flex items-end gap-2 h-40">
                  {stats.auxiliares.registros_por_mes.map((item, index) => {
                    const maxVal = Math.max(...stats.auxiliares.registros_por_mes.map(i => i.count));
                    const height = maxVal > 0 ? (item.count / maxVal) * 100 : 0;
                    return (
                      <div key={item.mes} className="flex-1 flex flex-col items-center">
                        <span className="text-xs text-gray-500 mb-1">{item.count}</span>
                        <div 
                          className="w-full bg-purple-500 rounded-t transition-all duration-500"
                          style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                        />
                        <span className="text-xs text-gray-400 mt-2 transform -rotate-45 origin-left">
                          {item.mes?.slice(5)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Sin datos de los últimos meses</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== RECURSERO ========== */}
      {activeTab === 'recursero' && (
        <div className="space-y-8">
          {/* Sección Institucional */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-amber-600" />
              Recursero Institucional
            </h2>
            
            {/* Cards instituciones */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StatCard 
                title="Total Instituciones" 
                value={stats.instituciones?.total || 0} 
                icon={Building} 
                color="amber"
              />
              <StatCard 
                title="Activas" 
                value={stats.instituciones?.activas || 0} 
                icon={UserCheck} 
                color="emerald"
              />
              <StatCard 
                title="Verificadas" 
                value={stats.instituciones?.verificadas || 0} 
                icon={Award} 
                color="blue"
              />
            </div>

            {/* Gráficos instituciones */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Building className="w-5 h-5 text-amber-600" />
                    Por Tipo de Institución
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart 
                    data={stats.instituciones?.por_tipo || []} 
                    labelKey="tipo" 
                    valueKey="count" 
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <PieChart className="w-5 h-5 text-indigo-600" />
                    Por Municipio
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart 
                    data={stats.instituciones?.por_municipio || []} 
                    labelKey="municipio" 
                    valueKey="count" 
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <BookOpen className="w-5 h-5 text-purple-600" />
                    Por Área de Trabajo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart 
                    data={stats.instituciones?.por_area || []} 
                    labelKey="area" 
                    valueKey="count" 
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Sección Personal */}
          <div className="pt-6 border-t">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Recursero Personal (Profesionales)
            </h2>
            
            {/* Cards personal */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <StatCard 
                title="Total en Recursero" 
                value={stats.recursero_personal?.total || 0} 
                icon={Users} 
                color="blue"
              />
              <StatCard 
                title="Con Empleo" 
                value={stats.recursero_personal?.con_empleo || 0} 
                subtitle={`${stats.recursero_personal?.total > 0 ? Math.round((stats.recursero_personal.con_empleo / stats.recursero_personal.total) * 100) : 0}% del total`}
                icon={UserCheck} 
                color="emerald"
              />
              <StatCard 
                title="Sin Empleo" 
                value={stats.recursero_personal?.sin_empleo || 0} 
                subtitle={`${stats.recursero_personal?.total > 0 ? Math.round((stats.recursero_personal.sin_empleo / stats.recursero_personal.total) * 100) : 0}% del total`}
                icon={UserX} 
                color="red"
              />
            </div>

            {/* Gráficos personal */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    Por Título
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart 
                    data={stats.recursero_personal?.por_titulo || []} 
                    labelKey="titulo" 
                    valueKey="count" 
                  />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Users className="w-5 h-5 text-pink-600" />
                    Por Género
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BarChart 
                    data={stats.recursero_personal?.por_genero || []} 
                    labelKey="genero" 
                    valueKey="count" 
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* ========== MATRICULADXS ========== */}
      {activeTab === 'matriculados' && (
        <div className="space-y-8">
          {/* Cards principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <StatCard 
              title="Total Matriculadxs" 
              value={stats.matriculados?.total || 0} 
              icon={Users} 
              color="blue"
            />
            <StatCard 
              title="Con Cuenta Creada" 
              value={stats.matriculados?.con_cuenta || 0} 
              subtitle={`${stats.matriculados?.total > 0 ? Math.round((stats.matriculados.con_cuenta / stats.matriculados.total) * 100) : 0}% del total`}
              icon={UserCheck} 
              color="emerald"
              onClick={fetchMatriculadosConCuenta}
            />
            <StatCard 
              title="Visibles en Recursero" 
              value={stats.matriculados?.en_recursero || 0} 
              subtitle={`${stats.matriculados?.total > 0 ? Math.round((stats.matriculados.en_recursero / stats.matriculados.total) * 100) : 0}% del total`}
              icon={TrendingUp} 
              color="purple"
            />
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <GraduationCap className="w-5 h-5 text-blue-600" />
                  Por Título
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.matriculados?.por_titulo || []} 
                  labelKey="titulo" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-pink-600" />
                  Por Género
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.matriculados?.por_genero || []} 
                  labelKey="genero" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>
          </div>

          {/* Gráficos de localidades */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="w-5 h-5 text-amber-600" />
                  Por Localidad de Residencia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.matriculados?.por_localidad_real || []} 
                  labelKey="localidad" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="w-5 h-5 text-indigo-600" />
                  Por Localidad Laboral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.matriculados?.por_localidad_laboral || []} 
                  labelKey="localidad" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>
          </div>

          {/* Gráfico de distritos */}
          {stats.matriculados?.por_distrito?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  Por Distrito
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BarChart 
                  data={stats.matriculados?.por_distrito || []} 
                  labelKey="distrito" 
                  valueKey="count" 
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Modal de Matriculados con Cuenta */}
      {showModalCuentas && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-emerald-600" />
                Matriculadxs con Cuenta Creada ({matriculadosConCuenta.length})
              </h2>
              <button 
                onClick={() => setShowModalCuentas(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {loadingModal ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : matriculadosConCuenta.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay matriculados con cuenta creada</p>
              ) : (
                <div className="space-y-2">
                  {matriculadosConCuenta.map((m, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div>
                        <p className="font-medium text-gray-900">{m.apellido}, {m.nombre}</p>
                        <p className="text-sm text-gray-500">
                          Mat. {m.matricula} • DNI {m.documento}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">{m.email}</p>
                        {m.fecha_registro && (
                          <p className="text-xs text-gray-400">
                            {new Date(m.fecha_registro).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t">
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setShowModalCuentas(false)}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== ESTADO DE CUENTA STATS ====================
function EstadoCuentaStats() {
  const [ciclos, setCiclos] = useState([]);
  const [cicloSeleccionado, setCicloSeleccionado] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(false);
  const [listaActiva, setListaActiva] = useState(null); // 'al_dia', 'deuda_leve', etc.
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [rebuildingHistorico, setRebuildingHistorico] = useState(false);
  const [reintentandoCiclo, setReintentandoCiclo] = useState(false);
  const [vistaCiclo, setVistaCiclo] = useState('deuda');

  useEffect(() => {
    cargarCiclos();
  }, []);

  // Auto-refresh cada 30 segundos si está activado
  useEffect(() => {
    let interval;
    if (autoRefresh && cicloSeleccionado) {
      interval = setInterval(() => {
        cargarEstadisticas(cicloSeleccionado);
        cargarCiclos();
      }, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, cicloSeleccionado]);

  const cargarCiclos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/estado-cuenta/ciclos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCiclos(res.data.ciclos || []);
      
      if (res.data.ciclos?.length > 0 && !cicloSeleccionado) {
        const cicloReciente = res.data.ciclos[0];
        setCicloSeleccionado(cicloReciente.mes);
        cargarEstadisticas(cicloReciente.mes);
      }
    } catch (error) {
      console.error('Error cargando ciclos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async (mes) => {
    if (!mes) return;
    setLoadingStats(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/antuar/estado-cuenta/estadisticas-ciclo/${mes}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEstadisticas(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      setEstadisticas(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const probarConexionAntuar = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data: d } = await axios.get(`${API_URL}/admin/antuar/test-conexion`,
        { headers: { Authorization: `Bearer ${token}` } });
      alert(d.exito
        ? `✅ Conexión con ANTUAR OK (${d.duracion_seg}s${d.via_proxy ? ' · vía proxy' : ''}).`
        : `❌ ANTUAR no responde: ${d.detalle_conexion || d.error}${d.ip_salida_servidor ? `\nIP de salida del servidor: ${d.ip_salida_servidor}` : ''}`);
    } catch (err) {
      alert('No se pudo probar la conexión: ' + (err.response?.data?.detail || err.message));
    }
  };

  const reintentarCicloMes = async () => {
    if (!window.confirm('Se relanzarán todas las tareas pendientes, reintentando o con error del ciclo del mes actual. '
      + 'No se crea un ciclo nuevo y no se reenvía a quienes ya recibieron.\n\n¿Continuar?')) return;
    const incluirDI = window.confirm('¿Incluir también las marcadas como "datos incorrectos"?\n\n'
      + 'Aceptar = se vuelven a consultar en ANTUAR · Cancelar = se dejan como están');
    setReintentandoCiclo(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/admin/estado-cuenta/reintentar-ciclo?incluir_datos_incorrectos=${incluirDI}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const d = res.data || {};
      const pe = d.por_estado || {};
      alert(d.relanzadas
        ? `Ciclo ${d.mes}: ${d.relanzadas} tarea(s) relanzada(s) (pendientes ${pe.pendiente || 0}, reintentando ${pe.reintentando || 0}, error ${pe.error || 0}`
          + `${d.atascadas_recuperadas ? `, ${d.atascadas_recuperadas} atascada(s) recuperada(s)` : ''}).\n`
          + (d.cola_iniciada ? 'La cola de envío se puso en marcha.' : d.cola_ya_activa ? 'La cola ya estaba en marcha.' : 'La cola no se inició: el envío de estado de cuenta está suspendido.')
        : `Ciclo ${d.mes}: no hay tareas para relanzar.`);
      refrescarDatos();
    } catch (err) {
      alert('No se pudo relanzar: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReintentandoCiclo(false);
    }
  };

  const refrescarDatos = () => {
    if (cicloSeleccionado) {
      cargarEstadisticas(cicloSeleccionado);
      cargarCiclos();
    }
  };

  // Reconstruye el rollup `estado_cuenta_stats_mensual` desde cero, escaneando
  // TODOS los meses disponibles en `estado_cuenta_tareas` (hasta 60).
  // Devuelve el resumen y luego el admin puede ver los gráficos actualizados
  // en /admin/deuda-evolucion.
  const reconstruirHistoricoDeuda = async () => {
    if (!window.confirm(
      'Vas a reconstruir el caché de estadísticas mensuales de deuda desde cero '
      + '(hasta 60 meses de historia). El proceso puede demorar unos segundos.\n\n¿Continuar?'
    )) return;
    setRebuildingHistorico(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/admin/estado-cuenta/stats-evolucion/rebuild?meses=60`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      const n = res.data?.meses_procesados ?? 0;
      alert(
        `Reconstrucción OK — se procesaron ${n} mes(es).\n\n`
        + `Ahora podés ver los gráficos actualizados en:\n`
        + `${window.location.origin}/admin/deuda-evolucion`
      );
    } catch (err) {
      alert('Error al reconstruir: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRebuildingHistorico(false);
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getNombreMes = (mesStr) => {
    if (!mesStr) return '';
    const [year, month] = mesStr.split('-');
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${meses[parseInt(month) - 1]} ${year}`;
  };

  // Función para exportar todas las categorías a CSV
  const exportarTodoCSV = (stats, mes) => {
    if (!stats?.distribucion_deuda) return;
    
    const categorias = [
      { key: 'al_dia', nombre: 'Al Día' },
      { key: 'deuda_leve', nombre: 'Deuda Leve' },
      { key: 'deuda_moderada', nombre: 'Deuda Moderada' },
      { key: 'deuda_grave', nombre: 'Deuda Grave' },
      { key: 'con_plan_pago', nombre: 'Con Plan de Pago' }
    ];
    
    const headers = ['Categoría', 'Matrícula', 'Apellido', 'Nombre', 'Email', 'Deuda Total', 'Deuda Vencida', 'Cuotas Vencidas'];
    const rows = [];
    
    categorias.forEach(cat => {
      const lista = stats.distribucion_deuda[cat.key]?.lista || [];
      lista.forEach(m => {
        rows.push([
          cat.nombre,
          m.matricula,
          m.apellido || '',
          m.nombre,
          m.email,
          m.deuda || 0,
          m.deuda_vencida || 0,
          m.cuotas_vencidas || 0
        ]);
      });
    });
    
    // Agregar resumen al final
    rows.push([]);
    rows.push(['RESUMEN']);
    rows.push(['Total Emails', stats.resumen.total_tareas]);
    rows.push(['Enviados', stats.resumen.completadas]);
    rows.push(['Pendientes', stats.resumen.pendientes]);
    rows.push(['Fallidos', stats.resumen.fallidas]);
    rows.push([]);
    rows.push(['DISTRIBUCIÓN']);
    categorias.forEach(cat => {
      const data = stats.distribucion_deuda[cat.key];
      rows.push([cat.nombre, data?.cantidad || 0, 'matriculados', '', formatMoney(data?.monto_total || 0), '', '']);
    });
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `estado_cuenta_completo_${mes}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (ciclos.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Mail className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No hay ciclos de estado de cuenta registrados</p>
          <p className="text-sm text-gray-400 mt-2">Los ciclos se crean automáticamente del 10 al 14 de cada mes</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con controles */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={refrescarDatos}
            disabled={loadingStats}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            <Loader2 className={`w-4 h-4 ${loadingStats ? 'animate-spin' : ''}`} />
            Actualizar datos
          </button>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={reconstruirHistoricoDeuda}
            disabled={rebuildingHistorico}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60"
            data-testid="btn-reconstruir-historico-deuda"
            title="Recalcula el caché de estadísticas mensuales de deuda desde cero (hasta 60 meses)"
          >
            <BarChart3 className={`w-4 h-4 ${rebuildingHistorico ? 'animate-pulse' : ''}`} />
            {rebuildingHistorico ? 'Reconstruyendo...' : 'Reconstruir Histórico de Deuda'}
          </button>
          <a
            href="/admin/deuda-evolucion"
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors"
            data-testid="link-dashboard-deuda"
          >
            <TrendingDown className="w-4 h-4" />
            Ver Dashboard de Evolución
          </a>
        </div>
        {estadisticas?.resumen?.pendientes > 0 && (
          <span className="text-sm text-amber-600 bg-amber-50 px-3 py-1 rounded-full">
            ⏳ Envío en curso: {estadisticas.resumen.pendientes} pendientes
          </span>
        )}
      </div>

      {/* Selector de ciclo */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Ciclos de Envío de Estado de Cuenta
            </CardTitle>
            <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={probarConexionAntuar} data-testid="btn-test-antuar">
              Probar conexión ANTUAR
            </Button>
            <Button size="sm" onClick={reintentarCicloMes} disabled={reintentandoCiclo}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-reintentar-ciclo"
              title="Relanza las tareas pendientes, reintentando o con error del ciclo del mes actual (no crea un ciclo nuevo)">
              <RefreshCw className={`w-4 h-4 mr-1 ${reintentandoCiclo ? 'animate-spin' : ''}`} />
              {reintentandoCiclo ? 'Relanzando...' : 'Reintentar ciclo del mes'}
            </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {ciclos.slice(0, 12).map((ciclo) => (
              <div
                key={ciclo.mes}
                onClick={() => {
                  setCicloSeleccionado(ciclo.mes);
                  setListaActiva(null);
                  cargarEstadisticas(ciclo.mes);
                }}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  cicloSeleccionado === ciclo.mes 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="font-semibold text-sm">{getNombreMes(ciclo.mes)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {ciclo.completadas}/{ciclo.total}
                </div>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${ciclo.porcentaje}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Estadísticas del ciclo */}
      {loadingStats ? (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      ) : estadisticas ? (
        <>
          {/* Vistas del ciclo: lo importante (deuda) primero; el seguimiento técnico del envío aparte */}
          <div className="flex flex-wrap gap-2 border-b" data-testid="ec-vistas-ciclo">
            {[
              { id: 'deuda', label: 'Distribución de deuda' },
              { id: 'envio', label: 'Seguimiento del envío',
                badge: (estadisticas.resumen.pendientes || 0) + (estadisticas.resumen.fallidas || 0) + (estadisticas.resumen.en_proceso || 0) },
            ].map(v => (
              <button key={v.id} type="button" onClick={() => setVistaCiclo(v.id)} data-testid={`ec-vista-${v.id}`}
                className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition-colors ${vistaCiclo === v.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
                {v.label}
                {v.badge > 0 && <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800">{v.badge}</span>}
              </button>
            ))}
          </div>

          {vistaCiclo === 'envio' && (
            <>
              {/* Resumen */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard 
                  title="Total Emails" 
                  value={estadisticas.resumen.total_tareas} 
                  icon={Mail} 
                  color="blue"
                />
                <StatCard 
                  title="Enviados" 
                  value={estadisticas.resumen.completadas} 
                  subtitle={`${estadisticas.resumen.porcentaje_completado}%`}
                  icon={CheckCircle} 
                  color="emerald"
                />
                <StatCard 
                  title="Pendientes" 
                  value={estadisticas.resumen.pendientes} 
                  subtitle={estadisticas.resumen.reintentando ? `incluye ${estadisticas.resumen.reintentando} reintentando` : undefined}
                  icon={Clock} 
                  color="amber"
                />
                <StatCard 
                  title="Fallidos" 
                  value={estadisticas.resumen.fallidas} 
                  icon={AlertCircle} 
                  color="red"
                />
              </div>

              <EstadoCuentaTareasPendientes tareas={estadisticas.tareas_no_completadas} />
              {!estadisticas.tareas_no_completadas?.length && (
                <p className="text-sm text-gray-500 text-center py-6">Todas las tareas del ciclo se completaron.</p>
              )}
            </>
          )}

          {vistaCiclo === 'deuda' && (
            <>
          {/* Distribución de deuda */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Distribución de Deuda - {getNombreMes(cicloSeleccionado)}
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">Hacé clic en cada categoría para ver el listado completo</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => exportarTodoCSV(estadisticas, cicloSeleccionado)}
                  className="flex items-center gap-1"
                >
                  <Download className="w-4 h-4" />
                  Exportar Todo
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div 
                  onClick={() => setListaActiva(listaActiva === 'al_dia' ? null : 'al_dia')}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-all ${
                    listaActiva === 'al_dia' 
                      ? 'bg-emerald-100 border-emerald-400 ring-2 ring-emerald-300' 
                      : 'bg-emerald-50 border-emerald-200 hover:border-emerald-400'
                  }`}
                >
                  <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-emerald-600">
                    {estadisticas.distribucion_deuda.al_dia.cantidad}
                  </p>
                  <p className="text-xs text-emerald-700">Al Día</p>
                  <p className="text-xs text-emerald-600 font-semibold">
                    {estadisticas.distribucion_deuda.al_dia.porcentaje}%
                  </p>
                </div>

                <div 
                  onClick={() => setListaActiva(listaActiva === 'deuda_leve' ? null : 'deuda_leve')}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-all ${
                    listaActiva === 'deuda_leve' 
                      ? 'bg-yellow-100 border-yellow-400 ring-2 ring-yellow-300' 
                      : 'bg-yellow-50 border-yellow-200 hover:border-yellow-400'
                  }`}
                >
                  <TrendingUp className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-yellow-600">
                    {estadisticas.distribucion_deuda.deuda_leve.cantidad}
                  </p>
                  <p className="text-xs text-yellow-700">Deuda Leve</p>
                  <p className="text-xs text-yellow-600 font-semibold">
                    {estadisticas.distribucion_deuda.deuda_leve.porcentaje}%
                  </p>
                </div>

                <div 
                  onClick={() => setListaActiva(listaActiva === 'deuda_moderada' ? null : 'deuda_moderada')}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-all ${
                    listaActiva === 'deuda_moderada' 
                      ? 'bg-orange-100 border-orange-400 ring-2 ring-orange-300' 
                      : 'bg-orange-50 border-orange-200 hover:border-orange-400'
                  }`}
                >
                  <TrendingUp className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-orange-600">
                    {estadisticas.distribucion_deuda.deuda_moderada.cantidad}
                  </p>
                  <p className="text-xs text-orange-700">Moderada</p>
                  <p className="text-xs text-orange-600 font-semibold">
                    {estadisticas.distribucion_deuda.deuda_moderada.porcentaje}%
                  </p>
                </div>

                <div 
                  onClick={() => setListaActiva(listaActiva === 'deuda_grave' ? null : 'deuda_grave')}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-all ${
                    listaActiva === 'deuda_grave' 
                      ? 'bg-red-100 border-red-400 ring-2 ring-red-300' 
                      : 'bg-red-50 border-red-200 hover:border-red-400'
                  }`}
                >
                  <TrendingDown className="w-6 h-6 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-600">
                    {estadisticas.distribucion_deuda.deuda_grave.cantidad}
                  </p>
                  <p className="text-xs text-red-700">Deuda Grave</p>
                  <p className="text-xs text-red-600 font-semibold">
                    {estadisticas.distribucion_deuda.deuda_grave.porcentaje}%
                  </p>
                </div>

                <div 
                  onClick={() => setListaActiva(listaActiva === 'con_plan_pago' ? null : 'con_plan_pago')}
                  className={`p-4 rounded-lg border text-center cursor-pointer transition-all ${
                    listaActiva === 'con_plan_pago' 
                      ? 'bg-blue-100 border-blue-400 ring-2 ring-blue-300' 
                      : 'bg-blue-50 border-blue-200 hover:border-blue-400'
                  }`}
                >
                  <FileText className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-blue-600">
                    {estadisticas.distribucion_deuda.con_plan_pago.cantidad}
                  </p>
                  <p className="text-xs text-blue-700">Con Plan</p>
                  <p className="text-xs text-blue-600 font-semibold">
                    {estadisticas.distribucion_deuda.con_plan_pago.porcentaje}%
                  </p>
                </div>
              </div>

              {/* Resumen financiero */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-3 text-gray-700">Resumen Financiero</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Deuda Detectada</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatMoney(estadisticas.montos.total_deuda_detectada)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Deuda Vencida</p>
                    <p className="text-xl font-bold text-red-600">
                      {formatMoney(estadisticas.montos.total_deuda_vencida)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Promedio por Matriculado</p>
                    <p className="text-xl font-bold text-gray-700">
                      {formatMoney(estadisticas.montos.promedio_deuda)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lista dinámica según selección */}
          {listaActiva && estadisticas.detalle[listaActiva]?.length > 0 && (
            <ListaMatriculados 
              tipo={listaActiva}
              lista={estadisticas.detalle[listaActiva]}
              formatMoney={formatMoney}
              onClose={() => setListaActiva(null)}
            />
          )}

            </>
          )}
        </>
      ) : null}
    </div>
  );
}

// Componente para mostrar lista de matriculados con ordenamiento y exportación
function ListaMatriculados({ tipo, lista, formatMoney, onClose }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const config = {
    al_dia: { title: 'Al Día (sin deuda vencida)', color: 'emerald', bgColor: 'bg-emerald-50', icon: CheckCircle },
    deuda_leve: { title: 'Deuda Vencida Leve (hasta $50k)', color: 'yellow', bgColor: 'bg-yellow-50', icon: TrendingUp },
    deuda_moderada: { title: 'Deuda Vencida Moderada ($50k-$150k)', color: 'orange', bgColor: 'bg-orange-50', icon: TrendingUp },
    deuda_grave: { title: 'Deuda Vencida Grave (+$150k)', color: 'red', bgColor: 'bg-red-50', icon: TrendingDown },
    con_plan_pago: { title: 'Con Plan de Pago', color: 'blue', bgColor: 'bg-blue-50', icon: FileText }
  };

  const cfg = config[tipo] || config.al_dia;
  const Icon = cfg.icon;

  // Función para ordenar
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Ordenar lista
  const listaSorted = [...lista].sort((a, b) => {
    if (!sortField) return 0;
    
    let valA = a[sortField];
    let valB = b[sortField];
    
    // Para campos numéricos
    if (['deuda', 'deuda_vencida', 'cuotas_vencidas'].includes(sortField)) {
      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
    } else {
      // Para campos de texto
      valA = String(valA || '').toLowerCase();
      valB = String(valB || '').toLowerCase();
    }
    
    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Función para exportar a CSV
  const exportarCSV = () => {
    const headers = tipo !== 'al_dia' 
      ? ['Matrícula', 'Apellido', 'Nombre', 'Email', 'Deuda Total', 'Deuda Vencida', 'Cuotas Vencidas']
      : ['Matrícula', 'Apellido', 'Nombre', 'Email'];
    
    if (tipo === 'con_plan_pago') {
      headers.push('Plan de Pago');
    }
    
    const rows = listaSorted.map(m => {
      const row = [m.matricula, m.apellido || '', m.nombre, m.email];
      if (tipo !== 'al_dia') {
        row.push(m.deuda || 0, m.deuda_vencida || 0, m.cuotas_vencidas || 0);
      }
      if (tipo === 'con_plan_pago') {
        row.push('Sí');
      }
      return row;
    });
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `estado_cuenta_${tipo}_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Componente para encabezado ordenable
  const SortableHeader = ({ field, label, align = 'left' }) => (
    <th 
      className={`text-${align} p-2 cursor-pointer hover:bg-gray-100 select-none transition-colors`}
      onClick={() => handleSort(field)}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''} ${align === 'center' ? 'justify-center' : ''}`}>
        {label}
        <span className="text-gray-400">
          {sortField === field ? (
            sortDirection === 'asc' ? '↑' : '↓'
          ) : '↕'}
        </span>
      </div>
    </th>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className={`flex items-center gap-2 text-${cfg.color}-600`}>
            <Icon className="w-5 h-5" />
            {cfg.title} ({lista.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportarCSV}
              className="flex items-center gap-1 text-xs"
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </Button>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Hacé click en los encabezados para ordenar
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-96 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className={`sticky top-0 bg-white`}>
              <tr className={`border-b ${cfg.bgColor}`}>
                <SortableHeader field="matricula" label="Matrícula" />
                <SortableHeader field="apellido" label="Apellido y Nombre" />
                <SortableHeader field="email" label="Email" />
                {tipo !== 'al_dia' && (
                  <>
                    <SortableHeader field="deuda" label="Deuda Total" align="right" />
                    <SortableHeader field="deuda_vencida" label="Vencida" align="right" />
                    <SortableHeader field="cuotas_vencidas" label="Cuotas" align="center" />
                  </>
                )}
                {tipo === 'con_plan_pago' && (
                  <th className="text-center p-2">Plan</th>
                )}
              </tr>
            </thead>
            <tbody>
              {listaSorted.map((m, idx) => (
                <tr key={idx} className={`border-b hover:${cfg.bgColor}`}>
                  <td className="p-2 font-medium">{m.matricula}</td>
                  <td className="p-2">{[m.apellido, m.nombre].filter(Boolean).join(', ')}</td>
                  <td className="p-2 text-gray-600 text-xs">{m.email}</td>
                  {tipo !== 'al_dia' && (
                    <>
                      <td className={`p-2 text-right font-bold text-${cfg.color}-600`}>
                        {formatMoney(m.deuda)}
                      </td>
                      <td className={`p-2 text-right text-${cfg.color}-500`}>
                        {formatMoney(m.deuda_vencida)}
                      </td>
                      <td className="p-2 text-center">
                        <span className={`bg-${cfg.color}-100 text-${cfg.color}-700 px-2 py-0.5 rounded text-xs font-medium`}>
                          {m.cuotas_vencidas}
                        </span>
                      </td>
                    </>
                  )}
                  {tipo === 'con_plan_pago' && (
                    <td className="p-2 text-center">
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">
                        ✓
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
          <span>Total: {lista.length} matriculados</span>
          {sortField && (
            <span className="text-xs">
              Ordenado por: {sortField} ({sortDirection === 'asc' ? 'ascendente' : 'descendente'})
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
